import { injectDateLang } from '../format/date-lang';
import { LocalizedDatePipe } from '../pipes/localized-date.pipe';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ApiErrorService } from '../../core/api-error';
import { AuthService } from '../../core/auth.service';
import { homeRouteFor } from '../../core/auth.guard';
import { SelectableMembership } from '../../models/auth.models';
import { BrandingService } from '../../core/branding.service';
import { TourEntry } from '../../core/tour/tour-entry.service';
import { SetupChecklistEntry } from '../../core/setup-checklist/setup-checklist-entry.service';
import { LayoutService } from '../../core/layout.service';
import { LocaleService, SUPPORTED_LOCALES } from '../../core/locale.service';
import { NotificationInboxItemDto, NotificationInboxService } from '../../core/notification-inbox.service';
import { primaryRole } from '../../core/primary-role';
import { WorkflowTasksBadgeService } from '../../core/workflow-tasks.service';
import { ShortcutDef, visibleShortcuts } from '../navigation/shortcut-access';
import { IconComponent } from '../icon/icon.component';
import { RoleChipListComponent } from '../role-chips/role-chip-list.component';
import { PendingInvitationsComponent } from '../pending-invitations/pending-invitations.component';
import { HelpGuidesPanelComponent } from '../help-panel/help-guides-panel.component';

/** Maps NotificationLog.RelatedEntityType (set by NotificationDispatchService callers) to the ops-console
 * route that shows that entity's detail page, so the notification dropdown can link straight to it. Entity
 * types with no detail route of their own (e.g. none yet) are simply omitted - canOpen()/openNotification()
 * treat those, and any related-entity-less notification, as non-interactive. */
export const ENTITY_DETAIL_ROUTES: Record<string, string> = {
  WorkOrder: 'work-orders',
  Asset: 'assets',
  MaintenanceRequest: 'maintenance-requests',
  Store: 'stores',
  PurchaseOrder: 'purchase-orders',
  // An approval request: the id is the workflow task's, and the page opens that task (/my-approvals/<taskId>).
  WorkflowTask: 'my-approvals',
  // A vendor job (calendar jobTask events, docs/design/29 §6); the Job detail route is /jobs/:id.
  Job: 'jobs',
  // A maintenance plan (calendar planned events, docs/design/30 §7); the plan detail route is /preventive-maintenance/:id.
  maintenancePlan: 'preventive-maintenance',
  // A service contract (the proposal sent to a provider and every other contract notification); /contracts/:id moves itself to /provider-contracts/:id for the provider side (docs/design/41 S8).
  ServiceContract: 'contracts',
  // A vendor of the client (e.g. the invited company registered and is linked); /vendors/:id.
  Vendor: 'vendors'
};

@Component({
  selector: 'app-shared-header',
  standalone: true,
  imports: [LocalizedDatePipe, CommonModule, TranslatePipe, RouterLink, IconComponent, PendingInvitationsComponent, RoleChipListComponent, HelpGuidesPanelComponent],
  templateUrl: './shared-header.component.html',
  styleUrl: './shared-header.component.scss'
})
export class SharedHeaderComponent implements OnInit {
  protected readonly dateLang = injectDateLang();
  readonly auth = inject(AuthService);
  readonly branding = inject(BrandingService);
  readonly layout = inject(LayoutService);
  /** The "?" menu (issue #861): explain this page (only on a page that has a tour, issue #836), all guides, and the setup checklist (only for a person who has one). */
  readonly tours = inject(TourEntry);
  /** On the dashboard, when the setup checklist is hidden, the same entry brings it back (issue #837). */
  readonly setupChecklist = inject(SetupChecklistEntry);
  private readonly router = inject(Router);
  private readonly locale = inject(LocaleService);
  private readonly translate = inject(TranslateService);
  private readonly notificationInbox = inject(NotificationInboxService);
  private readonly apiErrors = inject(ApiErrorService);
  private readonly approvalsBadge = inject(WorkflowTasksBadgeService);
  private readonly currentUrl = signal(this.router.url);
  /** Below 600px the shortcuts move into the user menu. */
  readonly narrow = signal(typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 599.98px)').matches);

  /** The personal shortcuts the user gets (approvals for the approver roles, My Tasks, Calendar), as the "My work" group of the sidebar used to list them. */
  readonly shortcuts = computed<ShortcutDef[]>(() => visibleShortcuts(this.auth, homeRouteFor(this.auth)));
  /** The waiting-approvals count on the approvals shortcut: empty at 0, "99+" above 99. */
  readonly approvalsBadgeText = computed(() => {
    const n = this.approvalsBadge.count();
    return n <= 0 ? '' : n > 99 ? '99+' : String(n);
  });

  readonly locales = SUPPORTED_LOCALES;
  readonly unreadCount = signal(0);
  /** The unread count of each OTHER organization, by organization id (docs/design/39 section 11.1); refreshed whenever the bell refreshes. */
  readonly otherUnread = signal<Record<string, number>>({});
  readonly isUserMenuOpen = signal(false);
  readonly isNotificationsOpen = signal(false);
  readonly isHelpOpen = signal(false);
  /** docs/design/38 section 9.1: the organization switcher (the brand block) - open state, the refusal text of a failed switch, and the switch in flight. */
  readonly isOrgMenuOpen = signal(false);
  readonly switchError = signal<string | null>(null);
  readonly switching = signal(false);
  readonly recentNotifications = signal<NotificationInboxItemDto[]>([]);
  readonly loadingNotifications = signal(false);

  @ViewChild('userMenu') private userMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('orgMenu') private orgMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('helpMenu') private helpMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('notificationsMenu') private notificationsMenuRef?: ElementRef<HTMLElement>;

  constructor() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const query = window.matchMedia('(max-width: 599.98px)');
      const onChange = (e: MediaQueryListEvent) => this.narrow.set(e.matches);
      query.addEventListener?.('change', onChange);
      inject(DestroyRef).onDestroy(() => query.removeEventListener?.('change', onChange));
    }
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.approvalsBadge.refresh();
      });
    // The count runs while the header (and so the signed-in shell) exists, and only for a user who gets the shortcut: the service itself never requests it for anyone else.
    this.approvalsBadge.start();
    inject(DestroyRef).onDestroy(() => this.approvalsBadge.stop());
  }

  /** True while the router sits on the shortcut's page (or below it). */
  isShortcutActive(shortcut: ShortcutDef): boolean {
    const url = this.currentUrl().split(/[?#]/)[0];
    return url === shortcut.path || url.startsWith(shortcut.path + '/');
  }

  ngOnInit(): void {
    this.notificationInbox.getUnreadCount().subscribe((result) => this.unreadCount.set(result.count));
    this.refreshOtherUnread();
    // The switcher data, read once after sign-in; SuperAdmin has no organization of their own. A failed read leaves the plain brand block.
    if (this.auth.currentUser()?.organizationId) {
      this.auth.fetchOrganizations().subscribe({ error: () => undefined });
      // The invitations waiting for the person (the login answer is gone after a reload); a failed read leaves what the login gave.
      this.auth.fetchInvitations().subscribe({ error: () => undefined });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.isUserMenuOpen() && !this.userMenuRef?.nativeElement.contains(target)) {
      this.isUserMenuOpen.set(false);
    }
    if (this.isOrgMenuOpen() && !this.orgMenuRef?.nativeElement.contains(target)) {
      this.isOrgMenuOpen.set(false);
    }
    if (this.isHelpOpen() && !this.helpMenuRef?.nativeElement.contains(target)) {
      this.isHelpOpen.set(false);
    }
    if (this.isNotificationsOpen() && !this.notificationsMenuRef?.nativeElement.contains(target)) {
      this.isNotificationsOpen.set(false);
    }
  }

  get currentLocale(): string {
    return this.auth.currentUser()?.preferredLocale ?? 'en';
  }

  /** SuperAdmin has no Organization of their own (docs/design/06_multi_tenant_organizations.md §1) - the
   * header falls back to a generic label rather than showing nothing. */
  get organizationLabel(): string {
    return this.auth.currentOrganizationName() ?? this.translate.instant('shared.allOrganizations');
  }

  /** A user can hold several roles; the header only has room for one, so it shows the most privileged (core/primary-role.ts -
   * the stored order means nothing). Display only: this has no effect on access control. */
  get primaryRoleLabel(): string | null {
    const role = primaryRole(this.auth.currentUser()?.roles);
    return role ? this.translate.instant(`shared.roles.${role}`) : null;
  }

  /** Every role the person holds, the most privileged first (display only). */
  get orderedRoles(): string[] {
    const roles = this.auth.currentUser()?.roles ?? [];
    const first = primaryRole(roles);
    return first ? [first, ...roles.filter((r) => r !== first)] : [...roles];
  }

  isProfileActive(): boolean {
    return this.currentUrl().split(/[?#]/)[0] === '/profile';
  }

  /** A language as the switch writes it: "ع" for Arabic, "EN" for English. */
  localeLabel(locale: string): string {
    return locale === 'ar' ? 'ع' : locale.toUpperCase();
  }

  /** The language the switch changes to: the other one of the two. */
  get targetLocale(): string {
    return this.currentLocale === 'ar' ? 'en' : 'ar';
  }

  switchLanguage(): void {
    this.isUserMenuOpen.set(false);
    this.isNotificationsOpen.set(false);
    this.setLocale(this.targetLocale);
  }

  /** "Switch organization" in the user menu opens the same panel as the brand block (the existing switch flow). */
  openOrganizationSwitcher(): void {
    this.isUserMenuOpen.set(false);
    this.switchError.set(null);
    this.isOrgMenuOpen.set(true);
  }

  /** No user-photo feature exists yet - initials drawn from the full name are the zero-backend-change
   * stand-in for an avatar. */
  get userInitials(): string {
    const name = this.auth.currentUser()?.fullName ?? '';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  /** The brand block is a dropdown for a person with two or more organizations to switch between, or with an invitation waiting (docs/design/39 section 7.1). */
  get hasOrgSwitcher(): boolean {
    return this.auth.organizations().length >= 2 || this.auth.invitations().length > 0;
  }

  isActiveOrganization(membership: SelectableMembership): boolean {
    return membership.organizationId === this.auth.currentUser()?.organizationId;
  }

  toggleOrgMenu(): void {
    this.isUserMenuOpen.set(false);
    this.isNotificationsOpen.set(false);
    this.switchError.set(null);
    this.isOrgMenuOpen.set(!this.isOrgMenuOpen());
  }

  switchOrganization(membership: SelectableMembership): void {
    if (this.switching()) return;
    if (this.isActiveOrganization(membership)) {
      this.isOrgMenuOpen.set(false);
      return;
    }
    this.switching.set(true);
    this.switchError.set(null);
    this.auth.switchOrganization(membership.membershipId).subscribe({
      next: () => {
        this.switching.set(false);
        this.isOrgMenuOpen.set(false);
        // A reload after the navigation, so no screen keeps the previous organization's data.
        void this.router.navigate([homeRouteFor(this.auth)], { replaceUrl: true }).then(() => this.reloadPage());
      },
      error: (err) => {
        this.switching.set(false);
        this.switchError.set(this.apiErrors.message(err));
      }
    });
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  toggleUserMenu(): void {
    this.isOrgMenuOpen.set(false);
    this.isNotificationsOpen.set(false);
    this.isHelpOpen.set(false);
    this.isUserMenuOpen.set(!this.isUserMenuOpen());
  }

  setLocale(locale: string): void {
    this.locale.apply(locale);
    this.auth.updatePreferredLocale(locale).subscribe();
  }

  toggleHelp(): void {
    this.isUserMenuOpen.set(false);
    this.isNotificationsOpen.set(false);
    this.isHelpOpen.update((open) => !open);
  }

  explainPage(): void {
    this.isHelpOpen.set(false);
    void this.tours.restartCurrent();
  }

  openGuides(): void {
    this.isHelpOpen.set(false);
    this.tours.openPanel();
  }

  openChecklist(): void {
    this.isHelpOpen.set(false);
    this.setupChecklist.reopen();
  }

  /** Opens the dropdown and loads the caller's recent notifications, regardless of unread count - it's
   * also a general "what have I gotten recently" view, not just an unread inbox. Marking read (and
   * clearing the badge) only happens if there actually was something unread; the fetched list's own
   * IsUnread flags are computed before that happens, so items still show which were new. */
  toggleNotifications(): void {
    this.isUserMenuOpen.set(false);
    this.isHelpOpen.set(false);
    const opening = !this.isNotificationsOpen();
    this.isNotificationsOpen.set(opening);
    if (!opening) {
      return;
    }

    this.refreshOtherUnread();
    this.loadingNotifications.set(true);
    this.notificationInbox.getRecent().subscribe({
      next: (items) => {
        this.recentNotifications.set(items);
        this.loadingNotifications.set(false);
      },
      error: () => this.loadingNotifications.set(false)
    });
    if (this.unreadCount() > 0) {
      this.notificationInbox.markAllRead().subscribe(() => this.unreadCount.set(0));
    }
  }

  /** A failed read keeps what is shown: the badge is a hint, never a reason to show an error. */
  private refreshOtherUnread(): void {
    if (!this.auth.currentUser()?.organizationId) return;
    this.notificationInbox.getUnreadCounts().subscribe({
      next: (rows) => this.otherUnread.set(Object.fromEntries((rows ?? []).map((r) => [r.organizationId, r.count]))),
      error: () => undefined
    });
  }

  unreadIn(membership: SelectableMembership): number {
    return this.otherUnread()[membership.organizationId] ?? 0;
  }

  /** The related record's real reference (its serial code, resolved by the API) - never a fragment of its id. Null when the
   * notification has none or the record is gone. */
  entityRefLabel(item: NotificationInboxItemDto): string | null {
    return item.reference || null;
  }

  /** The notification's title; a template key this build has no text for reads as a generic title, never the raw key. */
  notificationTitle(item: NotificationInboxItemDto): string {
    const key = `shared.notificationKeys.${item.templateKey}`;
    const text = this.translate.instant(key);
    return typeof text === 'string' && text && text !== key ? text : this.translate.instant('shared.notificationKeys.GENERIC');
  }

  canOpen(item: NotificationInboxItemDto): boolean {
    return !!item.relatedEntityType && !!item.relatedEntityId && item.relatedEntityType in ENTITY_DETAIL_ROUTES;
  }

  openNotification(item: NotificationInboxItemDto): void {
    if (!item.relatedEntityType || !item.relatedEntityId) {
      return;
    }
    const route = ENTITY_DETAIL_ROUTES[item.relatedEntityType];
    if (!route) {
      return;
    }
    this.isNotificationsOpen.set(false);
    // A request's notification may be "rate the work": the request page opens the rating form when there is one (and does nothing otherwise), as the mobile app does.
    if (item.relatedEntityType === 'MaintenanceRequest') {
      this.router.navigate([`/${route}`, item.relatedEntityId], { queryParams: { focus: 'rating' } });
      return;
    }
    this.router.navigate([`/${route}`, item.relatedEntityId]);
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login'], { replaceUrl: true });
  }
}
